import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { db, collections, ROLES } from '../../firebase.marketplace';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as marketplaceAuthService from '../../services/marketplaceAuthService';
import { SEOHead } from '../../components/SEO';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button as MuiButton, 
  TextField, 
  Typography, 
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { PhoneAndroid as PhoneIcon, Security as SecurityIcon } from '@mui/icons-material';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  padding: 1rem;
`;

const FormContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 10px;
  width: 100%;
  max-width: 400px;
  backdrop-filter: blur(10px);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  color: #4a90e2;
  font-weight: bold;
`;

const Title = styled.h2`
  color: white;
  text-align: center;
  margin-bottom: 2rem;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  width: 100%;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const Button = styled.button`
  padding: 0.8rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const GoogleButton = styled(Button)`
  background: #db4437;

  &:hover {
    background: #c1351d;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const Links = styled.div`
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 1rem;
  font-size: 0.9rem;

  a {
    color: #4a90e2;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // MFA related states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [mfaError, setMfaError] = useState(null);

  const { login, signInWithGoogle, currentUser, hasRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use the marketplace auth service instead of the general auth context
      const result = await marketplaceAuthService.signIn(formData.email, formData.password);
      
      // Check if MFA is required
      if (result.requiresMFA) {
        setMfaRequired(true);
        setMfaResolver(result.resolver);
        setLoading(false);
        return;
      }
      
      const user = result; // If MFA not required, result is the user
      
      // Show success message briefly before redirecting
      setSuccess('Sign in successful! Redirecting...');
      
      // Navigate to marketplace immediately to avoid permission errors
      setTimeout(() => {
        navigate('/marketplace');
      }, 1000);
      
      // Try to check roles, but don't block navigation if it fails
      try {
        const isArtist = await marketplaceAuthService.hasRole(user.uid, ROLES.ARTIST);
        const isAdmin = await marketplaceAuthService.hasRole(user.uid, ROLES.ADMIN);
        
        if (!isArtist && !isAdmin) {
          // Check if there's a pending artist request
          const artistRequest = await marketplaceAuthService.checkArtistRequest(user.uid);
          
          if (artistRequest) {
            console.log('User has a pending artist request');
          } else {
            console.log('User is a regular user');
          }
        }
      } catch (roleError) {
        // Log the error but don't prevent the user from accessing the app
        console.warn('Could not check user roles:', roleError);
      }
    } catch (err) {
      console.error('Login error in SignIn:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyMfaCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setMfaError('Please enter a valid verification code');
      return;
    }
    
    setVerifyingCode(true);
    setMfaError(null);
    
    try {
      // Verify the MFA code
      const user = await marketplaceAuthService.verifyMFACode(mfaResolver, verificationCode);
      
      // Close the MFA dialog
      setMfaRequired(false);
      setMfaResolver(null);
      setVerificationCode('');
      
      // Show success message briefly before redirecting
      setSuccess('Two-factor authentication successful! Redirecting...');
      
      // Navigate to marketplace
      setTimeout(() => {
        navigate('/marketplace');
      }, 1000);
    } catch (err) {
      console.error('MFA verification error:', err);
      setMfaError(err.message || 'Invalid verification code');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use the marketplace auth service for Google sign-in
      const user = await marketplaceAuthService.signInWithGoogle();
      
      // Show success message briefly before redirecting
      setSuccess('Sign in successful! Redirecting...');
      
      // Navigate to marketplace immediately to avoid permission errors
      setTimeout(() => {
        navigate('/marketplace');
      }, 1000);
      
      // Try to check roles, but don't block navigation if it fails
      try {
        const isArtist = await marketplaceAuthService.hasRole(user.uid, ROLES.ARTIST);
        const isAdmin = await marketplaceAuthService.hasRole(user.uid, ROLES.ADMIN);
        
        if (!isArtist && !isAdmin) {
          // Check if there's a pending artist request
          const artistRequest = await marketplaceAuthService.checkArtistRequest(user.uid);
          
          if (artistRequest) {
            console.log('User has a pending artist request');
          } else {
            console.log('User is a regular user');
          }
        }
      } catch (roleError) {
        // Log the error but don't prevent the user from accessing the app
        console.warn('Could not check user roles:', roleError);
      }
    } catch (err) {
      console.error('Google Sign In Error:', err);
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };
  
  // MFA Dialog component
  const MFADialog = () => (
    <Dialog 
      open={mfaRequired} 
      onClose={() => {}} // Prevent closing by clicking outside
      PaperProps={{
        style: {
          backgroundColor: '#0a1128',
          color: 'white',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxWidth: '400px',
          width: '100%'
        }
      }}
    >
      <DialogTitle style={{ textAlign: 'center' }}>
        <SecurityIcon style={{ fontSize: 40, color: '#4a90e2', marginBottom: '10px' }} />
        <Typography variant="h5">Two-Factor Authentication</Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body1" style={{ marginBottom: '20px', textAlign: 'center' }}>
            Enter the verification code sent to your phone to complete sign-in.
          </Typography>
          
          {mfaError && (
            <Alert 
              severity="error" 
              style={{ marginBottom: '15px', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
            >
              {mfaError}
            </Alert>
          )}
          
          <TextField
            label="Verification Code"
            variant="outlined"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="123456"
            inputProps={{ maxLength: 6 }}
            disabled={verifyingCode}
            autoFocus
            InputLabelProps={{
              style: { color: 'rgba(255, 255, 255, 0.7)' }
            }}
            InputProps={{
              style: { color: 'white' },
              startAdornment: <PhoneIcon style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.7)' }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4a90e2'
                }
              }
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions style={{ padding: '0 24px 20px', justifyContent: 'space-between' }}>
        <MuiButton 
          onClick={() => {
            setMfaRequired(false);
            setMfaResolver(null);
            setVerificationCode('');
          }}
          style={{ color: 'white' }}
          disabled={verifyingCode}
        >
          Cancel
        </MuiButton>
        
        <MuiButton 
          variant="contained" 
          color="primary"
          onClick={handleVerifyMfaCode}
          disabled={verifyingCode || !verificationCode || verificationCode.length < 6}
        >
          {verifyingCode ? <CircularProgress size={24} /> : 'Verify'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );

  if (authLoading) {
    return (
      <Container>
        <FormContainer>
          <Logo>KONZO</Logo>
          <Title>Loading...</Title>
        </FormContainer>
      </Container>
    );
  }

  return (
    <Container>
      <SEOHead
        title="Artist Sign In | Konzo Marketplace"
        description="Sign in to access your artist dashboard on Konzo Marketplace. Manage your music, tracks, and sales."
        keywords="artist login, konzo vibez, marketplace, music artist, sign in"
        canonicalUrl="/marketplace/auth/signin"
      />
      <FormContainer>
        <Logo>KONZO</Logo>
        <Title>Artist Sign In</Title>
        <Subtitle>Access your artist dashboard to manage your music and sales</Subtitle>
        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            name="email"
            placeholder="Artist Email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={loading}
            aria-label="Email"
          />
          <Input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={loading}
            aria-label="Password"
          />
          <Button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <GoogleButton type="button" onClick={handleGoogleSignIn} disabled={loading}>
            Sign In with Google
          </GoogleButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && (
            <div style={{ 
              color: '#4caf50', 
              textAlign: 'center', 
              marginTop: '1rem', 
              fontSize: '0.9rem',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              padding: '0.5rem',
              borderRadius: '5px'
            }}>
              {success}
            </div>
          )}
        </Form>
        <Links>
          <a href="/marketplace/auth/signup">Need an account? Sign Up</a>
          <a href="/marketplace/artist-registration">Become an Artist</a>
        </Links>
      </FormContainer>
    </Container>
  );
};

export default SignIn;
